import { z } from 'zod';
import { UNIT_AVAILABILITIES } from './enums.js';

/** Travel time in minutes; null clears the value (forms send the full object). */
const minutesField = z.number().int().min(0).max(6000).nullish();

/** Real-estate project master record as the backoffice edits it. */
export const ProjectInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  /** Markdown */
  description: z.string().max(50_000).nullish(),
  address: z.string().trim().min(1).max(500).nullish(),
  /** The developer's public site for this project (monitored for changes). */
  websiteUrl: z.string().trim().url().max(500).nullish(),
  lat: z.number().min(-90).max(90).nullish(),
  lng: z.number().min(-180).max(180).nullish(),
  yearOfCompletion: z.number().int().min(1800).max(2200).nullish(),
  /** Manual override; UI falls back to the live unit count when null. */
  numberOfUnits: z.number().int().min(0).max(100_000).nullish(),
  minutesShops: minutesField,
  minutesRestaurant: minutesField,
  minutesPublicTransport: minutesField,
  minutesSchool: minutesField,
  minutesHospital: minutesField,
  minutesSport: minutesField,
});
export type ProjectInput = z.infer<typeof ProjectInputSchema>;

export const UnitInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  /** Markdown */
  description: z.string().max(50_000).nullish(),
  /** Layout shorthand, e.g. "3+kk" */
  disposition: z.string().trim().min(1).max(50),
  price: z.number().nonnegative().max(1e12),
  availability: z.enum(UNIT_AVAILABILITIES).default('available'),
  hasTerraceBalcony: z.boolean(),
  hasParking: z.boolean(),
});
export type UnitInput = z.infer<typeof UnitInputSchema>;

export const ContactInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.string().trim().email().max(320).nullish(),
  phone: z.string().trim().max(50).nullish(),
  companyName: z.string().trim().max(200).nullish(),
  companyIco: z
    .string()
    .trim()
    .regex(/^\d{8}$/, 'IČO must be 8 digits')
    .nullish(),
  note: z.string().max(2000).nullish(),
});
export type ContactInput = z.infer<typeof ContactInputSchema>;

export const PhotoPatchSchema = z.object({
  /** Omit = keep, null = clear. */
  caption: z.string().max(500).nullish(),
  sortOrder: z.number().int().min(0).optional(),
});
export type PhotoPatch = z.infer<typeof PhotoPatchSchema>;
